import { Link } from 'react-router-dom';
import { formatDistance } from '@wineryxplorer/shared';
import type { DistanceLocale } from '@wineryxplorer/shared';
import type { WineryGroupData } from './WineryGroup';

interface CompactWineryGroupProps {
  group: WineryGroupData;
  highlighted?: boolean;
  distanceLocale?: DistanceLocale;
}

/**
 * Condensed WineryGroup for the Experiences mobile map bottom sheet: the
 * winery header plus one line per tasting, without the image cards.
 */
export const CompactWineryGroup = ({
  group,
  highlighted = false,
  distanceLocale = 'imperial',
}: CompactWineryGroupProps) => {
  const { venue, tastings } = group;
  const distance = formatDistance(venue.distanceMeters, distanceLocale);
  const hasCity = venue.city && venue.state;

  return (
    <section
      className={`rounded-lg bg-gray-50 border border-gray-200 p-3 transition-shadow ${
        highlighted ? 'shadow-lg' : 'shadow-sm'
      }`}
    >
      <Link to={`/wineries/${venue.id}`} className="block">
        <h3 className="font-serif text-lg font-normal leading-tight text-gray-900">{venue.name}</h3>
      </Link>
      <div className="mt-1 flex items-baseline gap-2 text-[0.7rem] font-sans tracking-widest uppercase text-gray-500">
        {hasCity && (
          <span>
            {venue.city}, {venue.state}
          </span>
        )}
        {hasCity && <span className="text-gray-300">·</span>}
        <span className="normal-case tracking-normal text-xs text-primary-700 font-medium">
          {distance.display}
        </span>
      </div>

      <ul className="mt-2 divide-y divide-gray-200">
        {tastings.map((t) => (
          <li key={t.id}>
            <Link
              to={`/tasting-experiences/${t.id}`}
              className="flex items-baseline justify-between gap-3 py-2 text-sm text-gray-700 hover:text-primary-700"
            >
              <span className="truncate">{t.name}</span>
              <span className="flex-shrink-0 text-gray-500">
                {t.wineClubOnly && t.price.amount === 0 ? 'Members' : `$${t.price.amount}`}
              </span>
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
};
